<template>
    <div>
        <!-- Only edit mode -->
        <div class="flex flex-wrap w-full gap-1" :class="marginsClass">
            <div
                v-for="(option, index) in selected"
                :key="option.name + index"
                class="px-[6px] bg-[#F2FAFF] rounded-full flex gap-[6px]"
            >
                <span class="text-won-blue text-[12px] leading-[15px]">{{ `${option.name}, (${option.shortCode})` }}</span>

                <span
                    class="cursor-pointer"
                    v-on:click="() => handleSelect(option)"
                >
                    <XIcon/>
                </span>
            </div>
        </div>
        
        <div v-if="allowEditing" class="relative w-full mt-2">
            <label :for="elementUUID" v-if="$slots.default" class="font-[Inter] block w-full text-sm font-semibold text-[#182333] mb-2">
                <slot />
            </label>

            <div class="relative">
                <input
                    @input="el => query = el.target.value"
                    :id="elementUUID"
                    :class="[inputStyle, 'h-9 rounded mt-0']"
                    :placeholder="placeholder"
                    :disabled="!searchable"
                    ref="input"
                    @click.prevent.stop="!opened && handleOpen()"
                />

                <ChevronRight
                    class="absolute top-2.5 right-2"
                    :class="opened ? '-rotate-90' : 'rotate-90'"
                />

                <div
                    v-if="!searchable"
                    :class="[
                        'absolute w-full h-full top-0 left-0 rounded-md cursor-pointer',
                        {
                            'border border-transparent ring-1 ring-green-800':
                                opened,
                        },
                    ]"
                    @click.prevent.stop="!opened && handleOpen()"
                />
                <div class="absolute inset-y-0 right-0 w-5 h-full flex items-center mr-2" v-if="searchable && loading">
                    <svg class="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            </div>

            <div
                v-click-outside="handleClose"
                v-if="opened && filteredOptions.length"
                :style="{ ...styles.dropdown, maxHeight: '200px' }"
                class="absolute left-0 top-full mt-px w-full rounded bg-white overflow-y-auto z-20"
            >
                <div
                    v-for="(option, index) in filteredOptions"
                    :key="index + option"
                    class="px-2.5 py-2 cursor-pointer text-sm transition hover:won-bg-gray-50"
                    @click="handleSelect(option)"
                >
                    <span class="truncate flex items-center">
                        <span :style="{ color: isOptionSelected(option) ? '#3070E8' : '' }">{{ `${option.name}, (${option.shortCode})` }}</span>
                        <svg
                            v-if="isOptionSelected(option)"
                            class="h-5 w-5 text-green-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="#3070E8"
                        >
                          <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                    </span>

                </div>
            </div>
        </div>

        <!-- Only view mode -->
        <div v-else>
            <div class="text-sm font-semibold text-gray-800 mb-2">
                <slot />
            </div>

            <div class="won-label-text-color text-sm mt-2">
                {{ modelValue }}
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import { inputStyle } from "../../utils/styles";
import { extractContent } from "../../utils/helpers";
import { v4 as uuid } from 'uuid'
import useDebouncedRef from '../../composables/debounceRef'
import axios from '../../utils/axios'
import CheckIcon from "../../components/interview-ai/create-interview/check-icon.vue";
import XIcon from "../../icons/x-icon.vue";
import {useNotifications} from "../../store/notifications.store";

import ChevronRight from "../../icons/chevron-right.vue"

const styles = {
    trigger: {
        border: "1px solid #DBDBDC",
    },
    active: {
        border: "1px solid #2A8176",
    },
    dropdown: {
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.08)",
        border: "1px solid #DBDBDC",
    },
};

const props = defineProps({
    searchable: {
        type: Boolean,
        default: false,
    },
    placeholder: {
        type: String,
        default: 'Select',
    },
    options: {
        type: Array,
        default: () => [],
    },
    url: {
        type: String,
        default: null,
    },
    modelValue: {
        type: String,
        default: undefined,
    },
    allowCustomValue: {
        type: Boolean,
        default: false,
    },
    allowEditing: {
        type: Boolean,
        default: true,
    },
    marginsClass: {
        type: String,
        default: 'mb-10'
    }
})

const emit = defineEmits(['update:modelValue', 'dropdown:onSelect'])

const input = ref(null)

const locations = ref([]);

const query = useDebouncedRef(props.modelValue || '', 300)

const fetchData = async () => {
    try {
        const response = await axios.get('/api/locations'); // Replace with your API endpoint
        locations.value = response.data; // Assuming the API response is an array of job functions
        filteredOptions.value = response.data;
    } catch (error) {
        console.error('Error fetching job functions:', error);
    }
};

onMounted(() => {
    // adding default value using ref
    fetchData();
    if(props.allowEditing) {
        input.value.value = props.modelValue || ''
    }
})

const opened = ref(false);
const elementUUID = ref(uuid())

const filteredOptions = ref( [])
const loading = ref(false)

const hardSelected = ref(false)
const selected = ref([]);

const notificationsStore = useNotifications();

watch(query, async(newVal) => {
    if(hardSelected.value) {
        hardSelected.value = false
        return
    }

    // if allow set custom value emit model value
    if (props.allowCustomValue) {
        emit('update:modelValue', newVal)
    }

    if (!props.searchable || !newVal) {
        filteredOptions.value = locations.value
        return filteredOptions.value;
    }

    // if URL provided make a ajax request
    if(props.url) {
        loading.value = true
        const { data: { data } } = await axios.get(`${props.url}?query=${newVal}`)
        loading.value = false
        filteredOptions.value = data.map(el => {
            return el.replace(newVal,`<span class="bg-gradient-to-r from-yellow-200 to-yellow-300 opacity-80">${newVal}</span>`);
        })
    } else {
        // filter option by keyword
        if (!newVal) {
            filteredOptions.value = locations.value;
        }
        filteredOptions.value = locations.value.filter((opt) =>
            opt.name.toLowerCase().includes(newVal.toLowerCase())
        )
    }
})

const handleOpen = () => {
    opened.value = true;
}

const handleClose = () => {
    opened.value = false;
}

const isOptionSelected = (option) => {
    const index = selected.value.findIndex(obj => obj.name === option.name);
    return index !== -1;

}

const handleSelect = (option) => {
    const indexToRemove = selected.value.findIndex(obj => obj.name === option.name);
    if (indexToRemove !== -1) {
        selected.value.splice(indexToRemove, 1);
    } else {
        if (selected.value.length >= 3) {
            notificationsStore.notify('Only 3 locations are allowed!', 'error')
        } else {
            selected.value.push(option);
        }
    }
    const functionNames = selected.value.map(item => item.name);
    emit('update:modelValue', functionNames)
}
</script>
